import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Checkbox,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility, VisibilityOff, InfoOutlined } from '@mui/icons-material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { useTheme } from '@mui/material/styles';
import axiosInstance from '../axios/axios';
import { END_POINTS } from '../shared';

interface UnenrollConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (password?: string) => void;
  agentName: string;
  agentHost?: string;
  showPassword?: boolean;
  isLoading?: boolean;
  agentId?: string;
  uninstallCommand?: string;
}

const UnenrollConfirmationModal: React.FC<UnenrollConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  agentName,
  agentHost,
  showPassword: requirePassword = false,
  isLoading = false,
  agentId,
  uninstallCommand,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [passwordValue, setPasswordValue] = useState('');
  const [showPasswordText, setShowPasswordText] = useState(false);

  // Automated execution states
  const [isForceUnenroll, setIsForceUnenroll] = useState(false);
  const [isAutomated, setIsAutomated] = useState(false);
  const [automatedIpAddress, setAutomatedIpAddress] = useState('');
  const [automatedUsername, setAutomatedUsername] = useState('');
  const [automatedPassword, setAutomatedPassword] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Password step state (shown when user clicks Yes in non-force path)
  const [showPasswordStep, setShowPasswordStep] = useState(false);

  // Scans check states
  const [inProgressCount, setInProgressCount] = useState<number | null>(null);
  const [activeScheduledCount, setActiveScheduledCount] = useState<number | null>(null);
  const [scansLoading, setScansLoading] = useState(false);

  // Reset states when modal opens
  useEffect(() => {
    if (open) {
      setPasswordValue('');
      setShowPasswordText(false);
      setIsForceUnenroll(false);
      setIsAutomated(false);
      setAutomatedIpAddress('');
      setAutomatedUsername('');
      setAutomatedPassword('');
      setShowPasswordStep(false);
    }
  }, [open]);

  // Fetch linked scans when open
  useEffect(() => {
    if (open && agentId) {
      setScansLoading(true);
      const timezone = 'Asia/Calcutta';
      axiosInstance
        .get(`${END_POINTS.NewAGENT_LINKED_SCANS}/${agentId}/scans?timezone=${timezone}`)
        .then((response) => {
          if (response.status === 200) {
            const allScans = response.data?.data?.all_scans || [];
            const scheduledScans = response.data?.data?.scheduled_scans || [];

            const inProgress = allScans.filter(
              (scan: any) => scan.status === 1 || scan.status === 2
            ).length;

            const activeScheduled = scheduledScans.filter(
              (scan: any) => scan.schedule?.schedule_status === 'Active'
            ).length;

            setInProgressCount(inProgress);
            setActiveScheduledCount(activeScheduled);
          }
        })
        .catch((error) => {
          console.error('Error fetching linked scans in confirmation modal:', error);
        })
        .finally(() => {
          setScansLoading(false);
        });
    } else {
      setInProgressCount(null);
      setActiveScheduledCount(null);
    }
  }, [open, agentId]);

  const handleClose = () => {
    onClose();
  };

  const handleCopyUninstall = (cmd: string) => {
    navigator.clipboard.writeText(cmd).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const defaultUninstallCmd = "sudo curl -o sparkagent_uninstall.sh https://spark-inventory.s3.us-east-1.amazonaws.com/scripts/sparkagent_uninstall.sh && sudo chmod +x sparkagent_uninstall.sh && sudo ./sparkagent_uninstall.sh";
  const displayUninstallCmd = uninstallCommand || defaultUninstallCmd;

  const handleConfirm = () => {
    if (isForceUnenroll) {
      if (isAutomated) {
        onConfirm(automatedPassword);
      } else {
        onConfirm();
      }
    } else {
      if (requirePassword) {
        onConfirm(passwordValue);
      } else {
        onConfirm();
      }
    }
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      fontSize: '13px',
      color: isDarkMode ? '#FFFFFF' : 'inherit',
      '& fieldset': {
        borderColor: isDarkMode ? '#4A5568' : '#CBD5E0',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#718096' : '#A0AEC0',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#7B73E8' : '#3A2ADA',
      },
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? '#A0AEC0' : '#718096',
      fontSize: '13px',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: isDarkMode ? '#7B73E8' : '#3A2ADA',
    },
  };

  // Shared button styles
  const cancelBtnSx = {
    minWidth: '100px',
    height: '34px',
    borderRadius: '6px',
    borderColor: isDarkMode ? '#4A5568' : '#d1d5db',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '13px',
    color: isDarkMode ? '#D1D5DB' : '#374151',
    '&:hover': {
      borderColor: isDarkMode ? '#718096' : '#9ca3af',
      backgroundColor: isDarkMode
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',
      color: isDarkMode ? '#FFFFFF' : '#1F2937',
    },
  };

  const primaryBtnSx = {
    minWidth: '100px',
    height: '34px',
    borderRadius: '6px',
    backgroundColor: '#3A2ADA',
    color: '#FFFFFF',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '13px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#2F1FBD',
      boxShadow: 'none',
    },
    '&:disabled': {
      backgroundColor: isDarkMode ? '#3D3D4E' : '#E5E7EB',
      color: isDarkMode ? '#7D7D8E' : '#9CA3AF',
    },
  };

  const noBtnSx = {
    minWidth: '64px',
    height: '32px',
    borderRadius: '6px',
    borderColor: '#3A2ADA',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '13px',
    px: 2,
    color: '#3A2ADA',
    '&:hover': {
      borderColor: '#2F1FBD',
      backgroundColor: 'rgba(58, 42, 218, 0.04)',
      color: '#2F1FBD',
    },
  };

  const yesBtnSx = {
    minWidth: '64px',
    height: '32px',
    borderRadius: '6px',
    backgroundColor: '#3A2ADA',
    color: '#FFFFFF',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '13px',
    px: 2,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#2F1FBD',
      boxShadow: 'none',
    },
  };

  const dialogPaperSx = {
    '& .MuiDialog-paper': {
      width: '470px',
      maxWidth: '90vw',
      borderRadius: '10px',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'hidden',
      backgroundColor: isDarkMode ? '#1E2330' : '#FFFFFF',
      boxShadow: isDarkMode
        ? '0px 8px 24px rgba(0, 0, 0, 0.5)'
        : '0px 8px 24px rgba(0, 0, 0, 0.12)',
    },
  };

  const isProceedDisabled = (() => {
    if (isForceUnenroll) {
      if (isAutomated) {
        return !automatedIpAddress || !automatedUsername || !automatedPassword;
      }
      return false;
    }
    return false;
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={dialogPaperSx}
      disableRestoreFocus
    >
      <DialogTitle
        sx={{
          textAlign: 'left',
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '24px',
          padding: '0 0 4px 0',
          color: isDarkMode ? '#FFFFFF' : '#111827',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Unenroll Agent
        <IconButton onClick={handleClose} size="small" sx={{ color: isDarkMode ? '#9CA3AF' : '#6B7280', padding: '4px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: '0',
          overflow: 'visible',
        }}
      >
        {/* Dummy inputs to prevent browser autofill from targeting background search fields */}
        <input
          type="text"
          name="chrome-autofill-dummy-username"
          style={{ position: 'absolute', top: -9999, left: -9999, width: 0, height: 0, opacity: 0 }}
          tabIndex={-1}
          autoComplete="off"
        />
        <input
          type="password"
          name="chrome-autofill-dummy-password"
          style={{ position: 'absolute', top: -9999, left: -9999, width: 0, height: 0, opacity: 0 }}
          tabIndex={-1}
          autoComplete="off"
        />
        
        {/* Warning banner if there are running/active scans */}
        {inProgressCount !== null && activeScheduledCount !== null && (inProgressCount > 0 || activeScheduledCount > 0) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '10px 12px',
              borderRadius: '6px',
              backgroundColor: isDarkMode ? 'rgba(247, 148, 31, 0.12)' : 'rgba(247, 148, 31, 0.06)',
              border: `1px solid ${isDarkMode ? '#92400E' : '#FBBF24'}`,
              mt: 1.5,
              mb: 1.5,
            }}
          >
            <WarningAmber
              sx={{
                color: isDarkMode ? '#FBBF24' : '#D97706',
                fontSize: '18px',
                flexShrink: 0,
                marginTop: '1px',
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: '18px',
                color: isDarkMode ? '#FCD34D' : '#92400E',
                fontWeight: 500,
              }}
            >
              This agent has {inProgressCount} scans currently in progress and {activeScheduledCount} active scheduled scans. Unenrolling the agent may interrupt running scans and prevent scheduled scans from executing.
            </Typography>
          </Box>
        )}

        {/* Confirmation text - hidden if Force Unenroll is checked */}
        {!isForceUnenroll && (
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '22px',
              color: isDarkMode ? '#D1D5DB' : '#374151',
              fontWeight: 400,
              mt: 1,
              mb: 1.5,
            }}
          >
            Do you want to unenroll agent{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                color: isDarkMode ? '#FFFFFF' : '#111827',
              }}
            >
              {agentName}
            </Box>
            ?
          </Typography>
        )}

        {/* Yes / No buttons - shown only when Force Unenroll is NOT checked and showPasswordStep is false */}
        {!isForceUnenroll && !showPasswordStep && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: '8px',
              mb: 1.5,
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isLoading}
              sx={noBtnSx}
            >
              No
            </Button>
            <Button
              onClick={() => {
                if (requirePassword) {
                  setShowPasswordStep(true);
                } else {
                  onConfirm();
                }
              }}
              variant="contained"
              disabled={isLoading}
              sx={yesBtnSx}
            >
              Yes
            </Button>
          </Box>
        )}

        {/* Password field and action buttons - shown when Yes is clicked */}
        {showPasswordStep && (
          <Box sx={{ mt: 1.5 }}>
            <TextField
              label="Password"
              type={showPasswordText ? 'text' : 'password'}
              fullWidth
              value={passwordValue}
              onChange={(e) => setPasswordValue(e.target.value)}
              size="small"
              autoComplete="new-password"
              inputRef={(input) => {
                if (input) {
                  setTimeout(() => input.focus(), 100);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswordText(!showPasswordText)}
                      edge="end"
                      size="small"
                      sx={{ color: isDarkMode ? '#A0AEC0' : '#718096' }}
                    >
                      {showPasswordText ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...textFieldStyles,
              }}
            />
          </Box>
        )}

        {/* Force Unenroll Agent Checkbox - hidden if showPasswordStep is true */}
        {!showPasswordStep && (
          <Box sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: 1.5 }}>
              <Checkbox
                checked={isForceUnenroll}
                onChange={(e) => {
                  setIsForceUnenroll(e.target.checked);
                  if (!e.target.checked) {
                    setIsAutomated(false);
                    setAutomatedUsername('');
                    setAutomatedPassword('');
                  }
                }}
                sx={{
                  padding: '0px',
                  color: isDarkMode ? '#6B7280' : '#9CA3AF',
                  '&.Mui-checked': {
                    color: isDarkMode ? '#7B73E8' : '#3A2ADA',
                  },
                }}
                size="small"
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '13px',
                  lineHeight: '20px',
                  color: isDarkMode ? '#E5E7EB' : '#111827',
                }}
              >
                Force Unenroll Agent
              </Typography>
            </Box>

            {/* Force Unenroll content - shown only when checkbox is checked */}
            {isForceUnenroll && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: isDarkMode ? '#161B26' : '#F3F4F6',
                    borderRadius: '6px',
                    padding: '4px',
                    width: 'fit-content',
                    mb: 1.5,
                  }}
                >
                  <Box
                    onClick={() => {
                      setIsAutomated(false);
                      setAutomatedIpAddress('');
                      setAutomatedUsername('');
                      setAutomatedPassword('');
                    }}
                    sx={{
                      padding: '6px 20px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      bgcolor: !isAutomated ? (isDarkMode ? '#2D3648' : '#FFFFFF') : 'transparent',
                      boxShadow: !isAutomated ? '0px 1px 2px rgba(0,0,0,0.1)' : 'none',
                      color: !isAutomated ? (isDarkMode ? '#7B73E8' : '#3A2ADA') : (isDarkMode ? '#9CA3AF' : '#6B7280'),
                      fontWeight: 600,
                      fontSize: '13px',
                      transition: 'all 0.2s',
                    }}
                  >
                    Manual
                  </Box>
                  <Box
                    // onClick={() => setIsAutomated(true)} // Disabled for now as it's not implemented
                    sx={{
                      padding: '6px 20px',
                      borderRadius: '4px',
                      cursor: 'not-allowed',
                      opacity: 0.5,
                      bgcolor: isAutomated ? (isDarkMode ? '#2D3648' : '#FFFFFF') : 'transparent',
                      boxShadow: isAutomated ? '0px 1px 2px rgba(0,0,0,0.1)' : 'none',
                      color: isAutomated ? (isDarkMode ? '#7B73E8' : '#3A2ADA') : (isDarkMode ? '#9CA3AF' : '#6B7280'),
                      fontWeight: 600,
                      fontSize: '13px',
                      transition: 'all 0.2s',
                    }}
                  >
                    Automatic
                  </Box>
                </Box>

                {!isAutomated && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: isDarkMode ? '#161B26' : '#F9FAFB',
                      border: `1px solid ${isDarkMode ? '#2D3648' : '#E5E7EB'}`,
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: 0.75 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '13px',
                            color: isDarkMode ? '#D1D5DB' : '#111827',
                          }}
                        >
                          Agent Uninstallation Command
                        </Typography>
                        <Tooltip title="Manual execution for agent unenrollment" arrow>
                          <InfoOutlined sx={{ fontSize: 15, color: isDarkMode ? '#6B7280' : '#9CA3AF', verticalAlign: 'middle' }} />
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: isDarkMode ? '#0F1219' : '#FFFFFF',
                          p: 1.5,
                          pr: 5,
                          borderRadius: '6px',
                          position: 'relative',
                          fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                          fontSize: '12px',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          color: isDarkMode ? '#d1d5db' : '#6B7280',
                        }}
                      >
                        <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyUninstall(displayUninstallCmd)}
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              right: 8,
                              transform: 'translateY(-50%)',
                              bgcolor: isDarkMode ? '#1E2330' : '#FFFFFF',
                              border: `1px solid ${isDarkMode ? '#2D3648' : '#E5E7EB'}`,
                              borderRadius: '5px',
                              width: 28,
                              height: 28,
                              '&:hover': {
                                bgcolor: isDarkMode ? '#2D3648' : '#F3F4F6',
                                borderColor: '#4640DE',
                              },
                            }}
                          >
                            <ContentCopy sx={{ fontSize: 14, color: isDarkMode ? '#9ca3af' : '#6B7280' }} />
                          </IconButton>
                        </Tooltip>
                        {displayUninstallCmd}
                      </Box>
                    </Box>
                  </Box>
                )}

                {isAutomated && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: 0.5 }}>
                      <InfoOutlined sx={{ fontSize: 16, color: isDarkMode ? '#9CA3AF' : '#6B7280' }} />
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: isDarkMode ? '#D1D5DB' : '#4B5563',
                        }}
                      >
                        Please provide valid SSH details
                      </Typography>
                    </Box>
                    <TextField
                      label="Ip Address"
                      fullWidth
                      value={automatedIpAddress}
                      onChange={(e) => setAutomatedIpAddress(e.target.value)}
                      size="small"
                      sx={textFieldStyles}
                    />
                    <TextField
                      label="Username"
                      fullWidth
                      value={automatedUsername}
                      onChange={(e) => setAutomatedUsername(e.target.value)}
                      size="small"
                      sx={textFieldStyles}
                    />
                    <TextField
                      label="Password"
                      type={showPasswordText ? 'text' : 'password'}
                      fullWidth
                      value={automatedPassword}
                      onChange={(e) => setAutomatedPassword(e.target.value)}
                      size="small"
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPasswordText(!showPasswordText)}
                              edge="end"
                              size="small"
                              sx={{ color: isDarkMode ? '#A0AEC0' : '#718096' }}
                            >
                              {showPasswordText ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={textFieldStyles}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* DialogActions at the bottom based on state */}
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          padding: '16px 0 0 0',
        }}
      >
        {showPasswordStep && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isLoading}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
            <Button
              onClick={() => onConfirm(passwordValue)}
              variant="contained"
              disabled={isLoading || !passwordValue}
              startIcon={
                isLoading ? (
                  <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
                ) : null
              }
              sx={{
                ...primaryBtnSx,
                backgroundColor: '#EF4444',
                '&:hover': {
                  backgroundColor: '#DC2626',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: isDarkMode ? '#3D3D4E' : '#E5E7EB',
                  color: isDarkMode ? '#7D7D8E' : '#9CA3AF',
                },
              }}
            >
              {isLoading ? 'Unenrolling...' : 'Unenroll'}
            </Button>
          </>
        )}
        
        {isForceUnenroll && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isLoading}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
            {isAutomated && (
              <Button
                onClick={handleConfirm}
                variant="contained"
                disabled={isLoading || isProceedDisabled}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
                  ) : null
                }
                sx={primaryBtnSx}
              >
                {isLoading ? 'Processing...' : 'Proceed'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UnenrollConfirmationModal;
